// src/components/common/StatsCards/StatsCards.jsx
import React from "react";
import { Loader2 } from "lucide-react";
import InfoIcon from "../InfoIcon/InfoIcon";
import styles from "./StatsCards.module.css";

/**
 * StatsCards Component
 *
 * @param {Object} props
 * @param {Array} props.stats - Statistics to display with structure:
 *   [{
 *     key: string,              // Unique identifier
 *     label: string,            // Display label
 *     value: number | string,   // Value to display
 *     icon?: ReactElement,      // Icon component
 *     color?: string,           // Icon/theme color (hex or named color)
 *     bgColor?: string,         // Background color for icon
 *     format?: function,        // Custom formatter (value) => string
 *     suffix?: string,          // Suffix to append (e.g., '%')
 *     prefix?: string,          // Prefix to prepend (e.g., '$')
 *     change?: number,          // Percentage change (shows trend indicator)
 *     changeType?: 'increase' | 'decrease', // Type of change
 *     onClick?: function,       // Click handler for interactive cards
 *     active?: boolean,         // Highlight as active/selected
 *     loading?: boolean,        // Show loading state for this stat
 *     hidden?: boolean,         // Hide this stat
 *     tooltip?: string          // Help text shown via an InfoIcon next to the label
 *   }]
 * @param {boolean} props.loading - Global loading state for all stats
 * @param {number} props.columns - Number of columns (auto-fit by default)
 * @param {string} props.minColumnWidth - Minimum width per column (default: '240px')
 * @param {string} props.gap - Gap between cards (default: '1.5rem')
 * @param {boolean} props.clickable - Make all cards clickable appearance
 * @param {string} props.valueFontSize - Override the .statValue font-size (default: unchanged)
 */
const StatsCards = ({
  stats = [],
  loading = false,
  columns,
  minColumnWidth = "240px",
  gap = "1.5rem",
  clickable = false,
  valueFontSize,
}) => {
  // Filter out hidden stats
  const visibleStats = stats.filter((stat) => !stat.hidden);

  // Format value
  const formatValue = (stat) => {
    if (stat.loading || loading) {
      return <Loader2 className={styles.statSpinner} size={20} />;
    }

    let formattedValue = stat.value;

    // Use custom formatter if provided
    if (stat.format) {
      formattedValue = stat.format(stat.value);
    } else {
      // Default formatting for numbers
      if (typeof stat.value === "number") {
        formattedValue = stat.value.toLocaleString();
      }
    }

    return (
      <>
        {stat.prefix && <span className={styles.prefix}>{stat.prefix}</span>}
        {formattedValue}
        {stat.suffix && <span className={styles.suffix}>{stat.suffix}</span>}
      </>
    );
  };

  // Get card class names
  const getCardClassName = (stat) => {
    const classes = [styles.statCard];

    if (stat.onClick || clickable) {
      classes.push(styles.clickable);
    }

    if (stat.active) {
      classes.push(styles.active);
    }

    return classes.join(" ");
  };

  // Grid style
  const gridStyle = {
    gap,
    gridTemplateColumns: columns
      ? `repeat(${columns}, 1fr)`
      : `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))`,
  };

  return (
    <div className={styles.statsGrid} style={gridStyle}>
      {visibleStats.map((stat) => {
        const Icon = stat.icon;
        const hasIcon = !!Icon;
        const hasChange = typeof stat.change === "number";

        return (
          <div
            key={stat.key}
            className={getCardClassName(stat)}
            onClick={stat.onClick}
            role={stat.onClick ? "button" : undefined}
            tabIndex={stat.onClick ? 0 : undefined}
            onKeyPress={(e) => {
              if (stat.onClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                stat.onClick();
              }
            }}
          >
            {hasIcon && (
              <div
                className={styles.statIcon}
                style={{
                  backgroundColor: stat.bgColor || `${stat.color}15`,
                  color: stat.color || "#7F5CFF",
                }}
              >
                <Icon size={24} />
              </div>
            )}

            <div className={styles.statContent}>
              <div className={`${styles.statLabel} ${stat.tooltip ? styles.statLabelWithTooltip : ''}`}>
                {stat.label}
                {stat.tooltip && <InfoIcon text={stat.tooltip} size={13} />}
              </div>
              <div
                className={styles.statValue}
                style={valueFontSize ? { fontSize: valueFontSize } : undefined}
              >
                {formatValue(stat)}
              </div>

              {hasChange && (
                <div
                  className={`${styles.statChange} ${
                    stat.changeType === "increase"
                      ? styles.increase
                      : styles.decrease
                  }`}
                >
                  <span className={styles.changeIndicator}>
                    {stat.changeType === "increase" ? "↑" : "↓"}
                  </span>
                  {Math.abs(stat.change)}%
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsCards;
